import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Icon } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { Provenance } from '@/components/money';
import { useWallets, type WalletSummary } from '@/api/hooks';
import { WalletTopUpDialog } from './wallets/WalletTopUpDialog';
import { KIND_LABELS, WalletBalance, ratePolicyText } from './wallets/walletView';
import { QueryState } from '@/components/QueryState';

function WalletCard({
  wallet,
  onOpen,
  onTopUp,
}: {
  wallet: WalletSummary;
  onOpen: () => void;
  onTopUp: () => void;
}) {
  return (
    <Card padding="md" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              font: '500 16px var(--font-display)',
              letterSpacing: 'var(--ls-heading)',
              color: 'var(--text-strong)',
            }}
          >
            {wallet.name}
          </div>
          <div
            style={{
              marginTop: 'var(--space-2)',
              font: '400 13px var(--font-text)',
              color: 'var(--text-subtle)',
            }}
          >
            {KIND_LABELS[wallet.kind] ?? wallet.kind}
          </div>
        </div>
        {wallet.isActive ? null : <Badge tone="neutral">Отключён</Badge>}
      </div>

      <WalletBalance wallet={wallet} />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
          paddingTop: 'var(--space-4)',
          borderTop: '1px solid var(--border-subtle)',
          marginTop: 'auto',
        }}
      >
        <Provenance>{ratePolicyText(wallet)}</Provenance>
        <span style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={onOpen}>
            Открыть
          </Button>
          {wallet.manualTopUps ? (
            <Button size="sm" onClick={onTopUp} iconLeft={<Icon name="Plus" size={16} />}>
              Пополнить
            </Button>
          ) : null}
        </span>
      </div>
    </Card>
  );
}

export function WalletsScreen() {
  const navigate = useNavigate();
  const wallets = useWallets();
  const [toppingUp, setToppingUp] = useState<WalletSummary | null>(null);

  const rows = wallets.data?.wallets ?? [];

  if (wallets.isLoading || wallets.isError) {
    return (
      <div className="kj-page">
        <QueryState query={wallets} what="кошельки" />
      </div>
    );
  }

  return (
    <div className="kj-page">
      {rows.length === 0 ? (
        <EmptyState
          icon="CreditCard"
          title="Кошельков пока нет"
          description="Кошельки описаны в коде и появляются при запуске сервера. Если список пуст, сервер ещё не применил реестр кошельков."
        />
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 'var(--space-5)',
            }}
          >
            {rows.map((wallet) => (
              <WalletCard
                key={wallet.id}
                wallet={wallet}
                onOpen={() => navigate(`/wallets/${wallet.id}`)}
                onTopUp={() => setToppingUp(wallet)}
              />
            ))}
          </div>

          <Provenance>
            Кошельки описаны в коде: новый кошелёк или источник данных добавляется доработкой, а не
            здесь.
          </Provenance>
        </>
      )}

      {toppingUp ? (
        <WalletTopUpDialog
          wallet={toppingUp}
          open={Boolean(toppingUp)}
          onClose={() => setToppingUp(null)}
        />
      ) : null}
    </div>
  );
}
